import LogoutIcon from '@mui/icons-material/Logout';
import { Typography } from '@mui/material';
import { clsx } from 'clsx';
import { format } from 'date-fns';
import React, { memo, useCallback, useState } from 'react';
import { NavLink, Outlet } from 'react-router';

import emailIcon from '~/assets/icons/email.svg';
import avatar from '~/assets/images/avatar.svg';
import logo from '~/assets/images/logo.svg';
import { LogoutModal } from '~/components/LogoutModal';
import { EXPIRE_DATE_FORMAT } from '~/constants/dateFormats';
import { ROUTES } from '~/constants/routes';
import { useAppSelector } from '~/store/hooks';
import { selectGeneratedLink } from '~/store/slices/generatedLink/selectors';
import { selectIsUserAuthenticated, selectUser } from '~/store/slices/user/selectors';

import styles from './styles.module.scss';

const Component: React.FC = () => {
  const isUserAuthenticated = useAppSelector(selectIsUserAuthenticated);
  const user = useAppSelector(selectUser);
  const generatedLink = useAppSelector(selectGeneratedLink);
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);

  const showLogoutModal = useCallback(() => {
    setLogoutModalVisible(true);
  }, []);

  const hideLogoutModal = useCallback(() => {
    setLogoutModalVisible(false);
  }, []);

  return (
    <>
      <div className={styles.page}>
        <header className={styles.page__header}>
          <div className={styles.page__column}>
            <NavLink className={styles['page__home-link']} to={ROUTES.HOME}>
              <img alt="logo" src={logo} />
            </NavLink>
            {isUserAuthenticated && (
              <>
                <NavLink
                  className={({ isActive }) =>
                    clsx(styles.page__link, { [styles['page__link--active']]: isActive })
                  }
                  to={ROUTES.COMPANIES}
                >
                  Companies
                </NavLink>
              </>
            )}
          </div>
          {isUserAuthenticated && (
            <div className={styles.page__column}>
              <Typography color="text.primary" mr={12} variant="subtitle1">
                {user?.first_name} {user?.last_name}
              </Typography>
              <img alt="avatar" src={avatar} />
              <div className={styles.page__separator} />
              <button aria-label="logout" type="button" onClick={showLogoutModal}>
                <LogoutIcon sx={{ color: 'var(--gray-2-color)' }} />
              </button>
            </div>
          )}
          {generatedLink && (
            <div>
              <div className={styles.page__email}>
                <img alt="email" src={emailIcon} />
                <Typography
                  color="var(--main-2-color)"
                  fontWeight={600}
                  lineHeight="24px"
                  sx={{ textDecoration: 'underline' }}
                  variant="subtitle2"
                >
                  {generatedLink.email}
                </Typography>
              </div>
              <Typography
                color="var(--gray-2-color)"
                display="block"
                fontWeight={600}
                lineHeight="24px"
                textAlign="right"
                variant="caption"
              >
                Available until {format(generatedLink.expiredAt, EXPIRE_DATE_FORMAT)}
              </Typography>
            </div>
          )}
        </header>
        <Outlet />
      </div>
      <LogoutModal open={logoutModalVisible} onClose={hideLogoutModal} />
    </>
  );
};

export const PageWithHeader = memo(Component);
